<template>
  <v-app>
    <the-nav-bar />
    <the-app-bar />
    <v-main>
      <transition name="page" mode="out-in">
        <router-view :key="$route.fullPath" />
      </transition>
    </v-main>
    <notification-modal />
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import TheAppBar from "./components/TheAppBar.vue";
import TheNavBar from "./components/TheNavBar.vue";
import NotificationModal from "./components/NotificationModal.vue";

export default {
  name: "App",
  components: {
    TheNavBar,
    TheAppBar,
    NotificationModal,
  },
  data: () => ({
    device_ip_address: "",
  }),
  computed: {
    ...mapGetters({
      progress_circular: "auth/progress_circular",
      devices: "device/devices",
      authenticated: "auth/authenticated",
    }),
  },
  watch: {
    // The stored-token check ("auth/attempt") resolves after the router has
    // already rendered the Login page, so move restored sessions along.
    authenticated(isAuthed) {
      if (isAuthed && this.$route.name === "Login") {
        this.$router.replace("/dashboard");
      }
    },
  },

  methods: {
    ...mapActions({
      insert_am_or_pm_to_attendance_table:
        "attendance/insert_am_or_pm_to_attendance_table",
      get_devices: "device/get_devices",
    }),
    ...mapMutations({
      set_progress: "auth/SET_PROGRESS_CIRCULAR",
    }),
  },
  created() {
    // Use a watcher on $route
    this.$watch(
      () => this.$route,
      (to) => {
        document.title = "JMB BIOMETRICS - " + to.name;
      }
    );

    if (this.authenticated && this.$route.name === "Login") {
      this.$router.replace("/dashboard");
    }
  },
};
</script>
<style>
html {
  overflow-y: auto;
  scroll-behavior: smooth;
}

/* ---------- Design tokens ---------- */
:root {
  --app-font: "Inter", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif;
  --surface: #ffffff;
  --surface-muted: #f8fafc;
  --border-subtle: #e2e8f0;
  --text-strong: #0f172a;
  --text-muted: #64748b;
  --shadow-soft: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 16px rgba(15, 23, 42, 0.06);
  --shadow-lifted: 0 4px 8px rgba(15, 23, 42, 0.06),
    0 12px 32px rgba(15, 23, 42, 0.12);
  --radius-md: 12px;
  --radius-lg: 16px;
  --ease-smooth: cubic-bezier(0.22, 1, 0.36, 1);
}

/* ---------- Base ---------- */
.v-application {
  font-family: var(--app-font) !important;
  background-color: #f1f5f9 !important;
  color: var(--text-strong);
}
.v-application .text-h1,
.v-application .text-h2,
.v-application .text-h3,
.v-application .text-h4,
.v-application .text-h5,
.v-application .text-h6,
.v-application .display-1,
.v-application .display-2,
.v-application .display-3,
.v-application .headline,
.v-application .title {
  font-family: var(--app-font) !important;
}

/* ---------- Page transitions ---------- */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.28s var(--ease-smooth),
    transform 0.28s var(--ease-smooth);
}
.page-enter {
  opacity: 0;
  transform: translateY(12px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Respect users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}

/* ---------- Buttons ---------- */
.v-application .v-btn {
  text-transform: none;
  letter-spacing: 0.01em;
  font-weight: 600;
  border-radius: 10px;
  transition: transform 0.18s var(--ease-smooth),
    box-shadow 0.18s var(--ease-smooth), background-color 0.18s ease,
    color 0.18s ease;
}
.v-application .v-btn:not(.v-btn--icon):not(.v-btn--fab):hover {
  transform: translateY(-1px);
}
.v-application .v-btn:not(.v-btn--icon):not(.v-btn--fab):active {
  transform: translateY(0);
}
.v-application .v-btn.primary {
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}
.v-application .v-btn.primary:hover {
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.45);
}

/* ---------- Cards & dialogs ---------- */
.v-application .v-card {
  border-radius: var(--radius-lg) !important;
  transition: box-shadow 0.25s var(--ease-smooth),
    transform 0.25s var(--ease-smooth);
}
.v-application .v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: var(--shadow-soft);
}
.v-dialog {
  border-radius: var(--radius-lg) !important;
  box-shadow: var(--shadow-lifted) !important;
}
.v-dialog > .v-card > .v-card__title {
  font-weight: 700;
  letter-spacing: -0.01em;
}

/* ---------- Data tables ---------- */
.v-application .v-data-table {
  border-radius: var(--radius-md) !important;
  border: 1px solid var(--border-subtle);
  box-shadow: none !important;
  overflow: hidden;
}
.v-application
  .v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th {
  background: var(--surface-muted);
  color: var(--text-muted) !important;
  text-transform: uppercase;
  font-size: 0.68rem !important;
  letter-spacing: 0.06em;
  font-weight: 700;
  white-space: nowrap;
}
.v-application
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(.v-data-table__empty-wrapper) {
  transition: background-color 0.15s ease;
}
.v-application
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(
    .v-data-table__empty-wrapper
  ) {
  background: #eef2ff !important;
}

/* ---------- Inputs ---------- */
.v-application .v-text-field--outlined fieldset {
  border-color: var(--border-subtle);
  transition: border-color 0.2s ease;
}
.v-application .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #6366f1;
}
.v-application .v-text-field--outlined > .v-input__control > .v-input__slot {
  border-radius: 10px;
}

/* ---------- Chips ---------- */
.v-application .v-chip {
  font-weight: 600;
  border-radius: 8px;
}

/* ---------- Scrollbar ---------- */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 8px;
  border: 2px solid #f1f5f9;
}
::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* ---------- Shared page layout helpers ---------- */
.page-shell {
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 16px 48px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.page-header__titles {
  flex: 1 1 auto;
  min-width: 220px;
}
.page-title {
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text-strong);
  line-height: 1.2;
}
.page-subtitle {
  margin-top: 4px;
  font-size: 0.9rem;
  color: var(--text-muted);
}
.page-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.content-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-soft);
  padding: 24px;
}
.content-card + .content-card {
  margin-top: 24px;
}
</style>
